import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  HiCheck,
  HiChevronDown,
  HiChevronRight,
  HiDownload,
  HiPencil,
  HiPlus,
  HiSearch,
  HiTrash,
  HiX
} from 'react-icons/hi';
import apiService from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSAO_MAP } from '../types';
import { getStatusColor, getStatusInfo } from '../utils/statusConfig';
import { SkeletonTable } from './Skeleton';
import ConfirmDialog from './ConfirmDialog';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';

const parseNumber = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const s = String(value)
    .replace(/R\$\s?/g, '')
    .replace(/\s/g, '')
    .replace(/kg/gi, '');
  let normalized = s;
  if (s.includes(',') && s.includes('.')) {
    normalized = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes(',')) {
    normalized = s.replace(',', '.');
  }
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
};

const calcValorUnitarioTributavel = (valor, pesoKg) => {
  const v = parseNumber(valor);
  const p = parseNumber(pesoKg);
  if (v == null || p == null || p <= 0) return null;
  return Math.round((v / p) * 1e6) / 1e6;
};

const displayCurrency = (value) => {
  if (value == null || value === '') return '—';
  const number = parseNumber(value);
  if (number == null) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const displayWeight = (value) => {
  const number = parseNumber(value);
  if (number == null) return '—';
  return `${number.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3
  })} kg`;
};

const displayQty = (value) => {
  const number = parseNumber(value);
  if (number == null) return '—';
  return Number.isInteger(number)
    ? String(number)
    : number.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
};

const displayTributavel = (value) => {
  const number = parseNumber(value);
  if (number == null) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(number);
};

const getStatusLabel = (status) => {
  if (!status) return '—';
  const info = getStatusInfo(status);
  if (info && info.label && info.label !== status) return info.label;
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getGroupInvoiceId = (group) => {
  for (const box of group?.remessas || []) {
    const id = box?.invoice?.id ?? box?.invoice_id ?? box?.fatura_id;
    if (id != null && id !== '' && Number.isFinite(Number(id))) return Number(id);
  }
  if (/^\d+$/.test(String(group?.key || ''))) return Number(group.key);
  return null;
};

const getItemDescricao = (item) => item?.descricao || item?.nome || item?.name || '';
const getItemQty = (item) => parseNumber(item?.quantidade ?? item?.quantity) ?? 1;
const getItemValor = (item) => parseNumber(item?.valor_unitario ?? item?.preco ?? item?.price);
const getItemPeso = (item) => parseNumber(item?.peso_unitario ?? item?.peso ?? item?.weight);
const getItemNcm = (item) => item?.ncm || item?.NCM || '';
const getItemCfop = (item) => item?.cfop || item?.CFOP || '';

const itensDaRemessa = (box) => {
  if (Array.isArray(box?.itens) && box.itens.length) return box.itens;
  if (Array.isArray(box?.produtos) && box.produtos.length) return box.produtos;
  return [];
};

const flattenOrigem = (group) => {
  const rows = [];
  for (const box of group?.remessas || []) {
    itensDaRemessa(box).forEach((item, index) => {
      if (item?.id == null) return;
      rows.push({
        key: `${box.id}-${item.id || index}`,
        box,
        item,
        compilado: false,
        item_numero: null
      });
    });
  }
  return rows;
};

const origemFromBoard = (board) => {
  const list = Array.isArray(board?.origem) ? board.origem : [];
  return list
    .filter((row) => row?.id != null)
    .map((row, index) => ({
      key: `api-${row.pacote_id || 'p'}-${row.id || index}`,
      box: {
        id: row.pacote_id,
        codigo_pacote: row.codigo_pacote,
        cliente: { nome: row.cliente, id: row.cliente_id },
        cliente_nome: row.cliente,
        cliente_id: row.cliente_id,
        notas_fiscais: Array.isArray(row.notas) ? row.notas : []
      },
      item: {
        id: row.id,
        descricao: row.descricao,
        nome: row.descricao,
        ncm: row.ncm,
        cfop: row.cfop,
        quantidade: row.quantidade,
        valor_unitario: row.valor_unitario,
        peso_unitario: row.peso_unitario,
        chave_acesso: row.chave_acesso
      },
      compilado: Boolean(row.compilado),
      item_numero: row.item_numero ?? null
    }));
};

const boxKey = (box) => String(box?.id ?? box?.codigo_pacote ?? 'cx');

const clienteKey = (box) =>
  String(box?.cliente_id ?? box?.cliente?.id ?? box?.cliente?.nome ?? box?.cliente_nome ?? '');

const findRemessa = (group, box) =>
  (group?.remessas || []).find(
    (r) => String(r.id) === String(box?.id) || (box?.codigo_pacote && r.codigo_pacote === box.codigo_pacote)
  );

const resolveOrigemRows = (group, board) => {
  const fromBoard = origemFromBoard(board);
  const rows = fromBoard.length > 0 ? fromBoard : flattenOrigem(group);
  return rows.map((row) => {
    const remessa = findRemessa(group, row.box);
    if (!remessa) return row;
    const notas =
      Array.isArray(row.box?.notas_fiscais) && row.box.notas_fiscais.length
        ? row.box.notas_fiscais
        : remessa.notas_fiscais;
    return {
      ...row,
      box: {
        ...remessa,
        ...row.box,
        notas_fiscais: notas,
        cliente: remessa.cliente || row.box.cliente,
        cliente_id: remessa.cliente_id ?? remessa.cliente?.id ?? row.box.cliente_id
      }
    };
  });
};

const groupOrigemRows = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    const id = boxKey(row.box);
    if (!map.has(id)) map.set(id, { box: row.box, rows: [] });
    map.get(id).rows.push(row);
  });
  return Array.from(map.values());
};

const roleFromPermissao = (userRole, user) => {
  const pid = Number(
    userRole?.permissaos_id ?? user?.permissaos_id ?? user?.permissao?.id
  );
  if (Number.isFinite(pid) && PERMISSAO_MAP[pid]) return PERMISSAO_MAP[pid];
  const named = String(
    userRole?.nome || user?.permissao?.permission || user?.permissao?.nome || ''
  ).trim();
  if (/admin/i.test(named)) return 'Admin';
  if (/contador/i.test(named)) return 'Contador';
  return named || null;
};

const normalizeChaveDigits = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length >= 44 ? digits.slice(0, 44) : digits;
};

const pushChave = (list, seen, raw) => {
  const chave = normalizeChaveDigits(raw?.chave || raw?.chave_acesso);
  if (!chave || seen.has(chave)) return;
  seen.add(chave);
  list.push({
    chave,
    document_id: raw?.document_id ?? raw?.id ?? null,
    nome: raw?.nome || raw?.nome_arquivo || raw?.descricao || 'NF',
    codigo_pacote: raw?.codigo_pacote || null
  });
};

const collectChavesLocais = (group) => {
  const seen = new Set();
  const list = [];
  for (const box of group?.remessas || []) {
    const notas = Array.isArray(box.notas_fiscais) ? box.notas_fiscais : [];
    notas.forEach((nf) => {
      pushChave(list, seen, { ...nf, codigo_pacote: box?.codigo_pacote });
    });
  }
  return list;
};

const collectChavesDaSelecao = (selectedRows, group, boardChaves = []) => {
  const seen = new Set();
  const fromItems = [];
  selectedRows.forEach(({ item, box }) => {
    if (item?.chave_acesso) {
      pushChave(fromItems, seen, {
        chave: item.chave_acesso,
        document_id: item.document_id,
        codigo_pacote: box?.codigo_pacote
      });
    }
    (box?.notas_fiscais || []).forEach((nf) => {
      pushChave(fromItems, seen, { ...nf, codigo_pacote: box?.codigo_pacote });
    });
  });
  if (fromItems.length > 0) return fromItems;

  const clientes = new Set(selectedRows.map(({ box }) => clienteKey(box)).filter(Boolean));
  const pacotes = new Set(selectedRows.map(({ box }) => box?.codigo_pacote).filter(Boolean));
  const fallback = [];
  const seenFb = new Set();
  (group?.remessas || []).forEach((box) => {
    const sameCliente = clientes.has(clienteKey(box));
    if (!sameCliente) return;
    (box.notas_fiscais || []).forEach((nf) => {
      pushChave(fallback, seenFb, { ...nf, codigo_pacote: box.codigo_pacote });
    });
  });
  (boardChaves || []).forEach((c) => {
    if (c.codigo_pacote && pacotes.has(c.codigo_pacote)) {
      pushChave(fallback, seenFb, c);
    }
  });
  return fallback;
};

const suggestFromSelection = (rows) => {
  let qtyTotal = 0;
  let valorPond = 0;
  let pesoPond = 0;
  let temValor = false;
  let temPeso = false;
  let descricao = '';
  let cfop = '';
  let ncm = '';
  const chavesFound = new Set();
  const docsFound = new Set();

  rows.forEach(({ item, box }) => {
    const qty = getItemQty(item);
    qtyTotal += qty;
    const valor = getItemValor(item);
    if (valor != null) {
      valorPond += valor * qty;
      temValor = true;
    }
    const peso = getItemPeso(item);
    if (peso != null) {
      pesoPond += peso * qty;
      temPeso = true;
    }
    if (!descricao) descricao = getItemDescricao(item);
    if (!cfop) cfop = getItemCfop(item);
    if (!ncm) ncm = getItemNcm(item);
    const fromItem = normalizeChaveDigits(item?.chave_acesso);
    if (fromItem) {
      chavesFound.add(fromItem);
    }
    (box?.notas_fiscais || []).forEach((nf) => {
      const digits = normalizeChaveDigits(nf?.chave_acesso);
      if (digits) chavesFound.add(digits);
      if (nf?.id) docsFound.add(String(nf.id));
    });
    if (item?.document_id) docsFound.add(String(item.document_id));
  });

  const valorUnit = temValor && qtyTotal > 0 ? valorPond / qtyTotal : null;
  const pesoUnit = temPeso && qtyTotal > 0 ? pesoPond / qtyTotal : null;
  const chaveUnica = chavesFound.size === 1 ? Array.from(chavesFound)[0] : '';

  return {
    descricao,
    cfop,
    ncm,
    quantidade: qtyTotal > 0 ? String(qtyTotal) : '',
    valor_unitario: valorUnit != null ? String(Number(valorUnit.toFixed(4))) : '',
    peso_unitario: pesoUnit != null ? String(Number(pesoUnit.toFixed(6))) : '',
    chave_acesso: chaveUnica,
    document_id: docsFound.size === 1 ? Array.from(docsFound)[0] : ''
  };
};

const emptyDraft = () => ({
  descricao: '',
  cfop: '',
  ncm: '',
  quantidade: '',
  valor_unitario: '',
  peso_unitario: '',
  chave_acesso: '',
  document_id: ''
});

const shortChave = (chave) => {
  const digits = normalizeChaveDigits(chave);
  if (!digits) return '';
  if (digits.length <= 12) return digits;
  return `${digits.slice(0, 6)} ··· ${digits.slice(-8)}`;
};

const linhaToForm = (linha) => ({
  descricao: linha.descricao || '',
  cfop: linha.cfop || '',
  ncm: linha.ncm || '',
  quantidade: linha.quantidade != null ? String(linha.quantidade) : '',
  valor_unitario: linha.valor_unitario != null ? String(linha.valor_unitario) : '',
  peso_unitario: linha.peso_unitario != null ? String(linha.peso_unitario) : '',
  chave_acesso: linha.chave_acesso || '',
  document_id: linha.document_id ? String(linha.document_id) : ''
});

const DocumentsCompilacao = ({ invoiceGroups, loadingPackages, showWarning, showSuccess }) => {
  const { user } = useAuth();
  const { userRole, loading: permissionsLoading } = usePermissions();
  const userPermissao = useMemo(
    () => roleFromPermissao(userRole, user),
    [userRole, user]
  );
  const isAdmin = userPermissao === 'Admin';
  const isContador = userPermissao === 'Contador';
  const canView = isAdmin || isContador || permissionsLoading;

  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [boards, setBoards] = useState({});
  const [selectedIds, setSelectedIds] = useState({});
  const [draft, setDraft] = useState(null);
  const [draftInvoiceId, setDraftInvoiceId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyDraft());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [invClearTarget, setInvClearTarget] = useState(null);
  const [hideUsed, setHideUsed] = useState(true);
  const [itemQuery, setItemQuery] = useState('');
  const [cxTab, setCxTab] = useState('all');
  const [composeKey, setComposeKey] = useState(null);
  const [composeStep, setComposeStep] = useState(1);

  const filteredGroups = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return invoiceGroups;
    return invoiceGroups.filter((group) => {
      if ((group.label || '').toLowerCase().includes(q)) return true;
      if ((group.destino || '').toLowerCase().includes(q)) return true;
      return (group.remessas || []).some((box) => {
        const codigo = String(box?.codigo_pacote || '').toLowerCase();
        const cliente = String(box?.cliente?.nome || box?.cliente_nome || '').toLowerCase();
        const itens = Array.isArray(box.itens) ? box.itens : [];
        return (
          codigo.includes(q) ||
          cliente.includes(q) ||
          itens.some((item) =>
            `${getItemDescricao(item)} ${getItemNcm(item)} ${getItemCfop(item)}`
              .toLowerCase()
              .includes(q)
          )
        );
      });
    });
  }, [invoiceGroups, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / perPage));
  const startIndex = (page - 1) * perPage;
  const paginated = filteredGroups.slice(startIndex, startIndex + perPage);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, perPage]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const loadBoard = useCallback(
    async (invoiceId) => {
      if (!invoiceId) return;
      setBoards((prev) => ({
        ...prev,
        [invoiceId]: { ...(prev[invoiceId] || {}), loading: true }
      }));
      try {
        const json = await apiService.getInvoiceCompilacao(invoiceId);
        const data = json?.data || json;
        setBoards((prev) => ({
          ...prev,
          [invoiceId]: {
            loading: false,
            linhas: data?.linhas || [],
            origem: data?.origem || [],
            chaves: data?.chaves || [],
            pode_compilar: Boolean(data?.pode_compilar)
          }
        }));
      } catch (error) {
        setBoards((prev) => ({
          ...prev,
          [invoiceId]: { loading: false, linhas: [], origem: [], chaves: [], error: error.message }
        }));
        showWarning(error.message || 'Erro ao carregar compilação');
      }
    },
    [showWarning]
  );

  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || loadingPackages || !filteredGroups.length) return;
    const first = filteredGroups[0];
    autoOpened.current = true;
    setExpanded({ [first.key]: true });
    const invoiceId = getGroupInvoiceId(first);
    if (invoiceId) loadBoard(invoiceId);
  }, [loadingPackages, filteredGroups, loadBoard]);

  const boardsRef = useRef(boards);
  boardsRef.current = boards;
  useRealtimeRefresh(
    ['invoices', 'products', 'documents'],
    () => {
      Object.keys(boardsRef.current).forEach((id) => loadBoard(Number(id)));
    },
    { paused: Boolean(draft || editingId || composeKey) }
  );

  const toggleGroup = (group) => {
    const key = group.key;
    const next = !expanded[key];
    setExpanded((prev) => ({ ...prev, [key]: next }));
    const invoiceId = getGroupInvoiceId(group);
    if (next && invoiceId) loadBoard(invoiceId);
  };

  const usedProdutoIds = (invoiceId) => {
    const linhas = boards[invoiceId]?.linhas || [];
    const ids = new Set();
    linhas.forEach((linha) => {
      (linha.produto_ids || []).forEach((id) => ids.add(Number(id)));
    });
    return ids;
  };

  const syncSelection = (invoiceId, group, nextIds, used) => {
    const clean = Array.from(new Set(nextIds.map(Number))).filter(
      (id) => Number.isFinite(id) && !used.has(id)
    );
    setSelectedIds((prev) => ({ ...prev, [invoiceId]: clean }));
    if (clean.length === 0) {
      setDraft(null);
      setDraftInvoiceId(null);
      return;
    }
    const idSet = new Set(clean);
    const rows = resolveOrigemRows(group, boards[invoiceId]).filter(({ item }) =>
      idSet.has(Number(item.id))
    );
    setDraftInvoiceId(invoiceId);
    const suggested = suggestFromSelection(rows);
    const related = collectChavesDaSelecao(rows, group, boards[invoiceId]?.chaves || []);
    if (!suggested.chave_acesso && related.length === 1) {
      suggested.chave_acesso = related[0].chave;
      suggested.document_id = related[0].document_id ? String(related[0].document_id) : '';
    }
    setDraft(suggested);
  };

  const setBoxSelection = (invoiceId, group, rows, used, checked) => {
    const cur = new Set((selectedIds[invoiceId] || []).map(Number));
    rows.forEach(({ item }) => {
      const id = Number(item.id);
      if (used.has(id)) return;
      if (checked) cur.add(id);
      else cur.delete(id);
    });
    syncSelection(invoiceId, group, Array.from(cur), used);
  };

  const toggleItem = (invoiceId, group, itemId, used) => {
    if (used.has(Number(itemId))) return;
    const cur = new Set((selectedIds[invoiceId] || []).map(Number));
    const id = Number(itemId);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    syncSelection(invoiceId, group, Array.from(cur), used);
  };

  const clearSelection = (invoiceId) => {
    setSelectedIds((prev) => ({ ...prev, [invoiceId]: [] }));
    setDraft(null);
    setDraftInvoiceId(null);
  };

  const pickChave = (setter, chaves, chave) => {
    const match = chaves.find((c) => c.chave === chave);
    setter((d) => ({
      ...d,
      chave_acesso: d.chave_acesso === chave ? '' : chave,
      document_id: d.chave_acesso === chave ? '' : match?.document_id ? String(match.document_id) : ''
    }));
  };

  const submitComposer = async (group) => {
    const invoiceId = draftInvoiceId || getGroupInvoiceId(group);
    if (!invoiceId || !draft) return;
    const selected = (selectedIds[invoiceId] || []).map(Number);
    setSaving(true);
    try {
      await apiService.createInvoiceCompilacao(invoiceId, {
        produto_ids: selected,
        descricao: draft.descricao || null,
        cfop: draft.cfop || null,
        ncm: draft.ncm || null,
        quantidade: parseNumber(draft.quantidade),
        valor_unitario: parseNumber(draft.valor_unitario),
        peso_unitario: parseNumber(draft.peso_unitario),
        chave_acesso: draft.chave_acesso || null,
        document_id: draft.document_id ? Number(draft.document_id) : null
      });
      showSuccess('Linha compilada na invoice');
      setDraft(null);
      setDraftInvoiceId(null);
      setSelectedIds((prev) => ({ ...prev, [invoiceId]: [] }));
      setComposeStep(1);
      await loadBoard(invoiceId);
    } catch (error) {
      showWarning(error.message || 'Erro ao compilar');
    } finally {
      setSaving(false);
    }
  };

  const saveLinha = async (linha) => {
    setSaving(true);
    try {
      await apiService.updateInvoiceCompilacao(linha.id, {
        descricao: editForm.descricao || null,
        cfop: editForm.cfop || null,
        ncm: editForm.ncm || null,
        quantidade: parseNumber(editForm.quantidade),
        valor_unitario: parseNumber(editForm.valor_unitario),
        peso_unitario: parseNumber(editForm.peso_unitario),
        chave_acesso: editForm.chave_acesso || null,
        document_id: editForm.document_id ? Number(editForm.document_id) : null
      });
      showSuccess('Linha atualizada');
      setEditingId(null);
      await loadBoard(linha.invoice_id);
    } catch (error) {
      showWarning(error.message || 'Erro ao salvar linha');
    } finally {
      setSaving(false);
    }
  };

  const toggleFlag = async (linha, field, value) => {
    if (field === 'inv_ok' && !isAdmin) return;
    if (field === 'nf_ok' && !linha.inv_ok && value) {
      showWarning('Marque INV antes de marcar NF');
      return;
    }
    if (field === 'inv_ok' && !value && linha.nf_ok) {
      setInvClearTarget(linha);
      return;
    }
    try {
      await apiService.updateInvoiceCompilacao(linha.id, { [field]: value });
      await loadBoard(linha.invoice_id);
    } catch (error) {
      showWarning(error.message || 'Não foi possível atualizar o check');
    }
  };

  const confirmClearInv = async () => {
    if (!invClearTarget) return;
    const linha = invClearTarget;
    setInvClearTarget(null);
    if (editingId === linha.id) setEditingId(null);
    try {
      await apiService.updateInvoiceCompilacao(linha.id, { inv_ok: false });
      await loadBoard(linha.invoice_id);
    } catch (error) {
      showWarning(error.message || 'Não foi possível desmarcar INV');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteInvoiceCompilacao(deleteTarget.id);
      showSuccess('Linha removida');
      const invoiceId = deleteTarget.invoice_id;
      setDeleteTarget(null);
      await loadBoard(invoiceId);
    } catch (error) {
      showWarning(error.message || 'Erro ao excluir linha');
    }
  };

  const downloadXls = async (group) => {
    const invoiceId = getGroupInvoiceId(group);
    if (!invoiceId) return;
    try {
      const nome = `Invoice-${group.label || invoiceId}.xls`;
      await apiService.downloadInvoiceCompilacaoXls(invoiceId, nome);
    } catch (error) {
      showWarning(error.message || 'Erro ao baixar XLS');
    }
  };

  const getCompileState = (group) => {
    const invoiceId = getGroupInvoiceId(group);
    const board = invoiceId ? boards[invoiceId] : null;
    const linhas = board?.linhas || [];
    const origemRows = resolveOrigemRows(group, board);
    const used = new Set(invoiceId ? [...usedProdutoIds(invoiceId)] : []);
    origemRows.forEach((row) => {
      if (row.compilado && row.item?.id != null) used.add(Number(row.item.id));
    });
    const selected = new Set((selectedIds[invoiceId] || []).map(Number));
    const availableRows = origemRows.filter(({ item }) => !used.has(Number(item.id)));
    const availableCount = availableRows.length;
    const qItem = itemQuery.trim().toLowerCase();
    const selectedRows = origemRows.filter(({ item }) => selected.has(Number(item.id)));
    const cxGroups = groupOrigemRows(origemRows);
    const visibleRows = origemRows.filter((row) => {
      const itemId = Number(row.item.id);
      const isUsed = used.has(itemId);
      if (hideUsed && isUsed) return false;
      if (cxTab !== 'all' && boxKey(row.box) !== String(cxTab)) return false;
      if (!qItem) return true;
      const hay = `${row.box?.codigo_pacote || ''} ${getItemDescricao(row.item)} ${getItemNcm(row.item)} ${getItemCfop(row.item)}`.toLowerCase();
      return hay.includes(qItem);
    });
    const grouped = groupOrigemRows(visibleRows);
    const allVisibleAvailable = visibleRows.filter(({ item }) => !used.has(Number(item.id)));
    const allAvailableChecked =
      allVisibleAvailable.length > 0 &&
      allVisibleAvailable.every(({ item }) => selected.has(Number(item.id)));
    const canCompile = isAdmin || board?.pode_compilar === true;
    const invCount = linhas.filter((l) => l.inv_ok).length;
    const nfCount = linhas.filter((l) => l.nf_ok).length;
    const chaves = [
      ...(board?.chaves || []),
      ...collectChavesLocais(group).filter(
        (c) => !(board?.chaves || []).some((x) => x.chave === c.chave)
      )
    ];
    const chavesSelecao = collectChavesDaSelecao(selectedRows, group, chaves);
    const composerOpen = Boolean(draft && draftInvoiceId === invoiceId && selected.size > 0);
    const tributavelDraft = calcValorUnitarioTributavel(
      draft?.valor_unitario,
      draft?.peso_unitario
    );
    const compiledPct =
      origemRows.length > 0
        ? Math.round(((origemRows.length - availableCount) / origemRows.length) * 100)
        : 0;
    const valorTotal = linhas.reduce((sum, linha) => {
      const q = parseNumber(linha.quantidade) ?? 1;
      const v = parseNumber(linha.valor_unitario);
      if (v == null) return sum;
      return sum + v * q;
    }, 0);
    return {
      invoiceId,
      board,
      linhas,
      origemRows,
      used,
      selected,
      availableCount,
      selectedRows,
      cxGroups,
      visibleRows,
      grouped,
      allVisibleAvailable,
      allAvailableChecked,
      canCompile,
      invCount,
      nfCount,
      chaves,
      chavesSelecao,
      composerOpen,
      tributavelDraft,
      compiledPct,
      valorTotal
    };
  };

  const openCompose = (group) => {
    setCxTab('all');
    setItemQuery('');
    setComposeStep(1);
    setComposeKey(group.key);
    const invoiceId = getGroupInvoiceId(group);
    if (invoiceId) loadBoard(invoiceId);
  };

  const closeCompose = () => {
    const group = invoiceGroups.find((g) => g.key === composeKey);
    if (group) {
      const invoiceId = getGroupInvoiceId(group);
      if (invoiceId) clearSelection(invoiceId);
    }
    setComposeKey(null);
    setComposeStep(1);
  };

  useEffect(() => {
    if (!composeKey) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') closeCompose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeKey]);

  if (!canView && !permissionsLoading) {
    return (
      <div className="card">
        <div className="empty-state">
          <p>A compilação da invoice é visível para administrador e contador.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-actions cmp-page-actions">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar invoice, destino ou CX..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {isAdmin ? (
          <p className="cmp-lead">Abra a invoice e use Nova linha para selecionar os itens.</p>
        ) : (
          <p className="cmp-lead">Revise as linhas compiladas e marque NF depois do INV.</p>
        )}
      </div>

      <div className="card">
        {loadingPackages ? (
          <SkeletonTable rows={8} columns={6} />
        ) : filteredGroups.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm
                ? 'Nenhuma invoice encontrada para a busca'
                : 'Nenhuma remessa com Invoice encontrada'}
            </p>
          </div>
        ) : (
          <>
            <div className="documents-client-list">
              {paginated.map((group) => {
                const isOpen = !!expanded[group.key];
                const {
                  board,
                  linhas,
                  origemRows,
                  availableCount,
                  canCompile,
                  invCount,
                  nfCount,
                  chaves,
                  compiledPct,
                  valorTotal
                } = getCompileState(group);
                const statusLabel = getStatusLabel(group.status);
                const statusColor = getStatusColor(group.status) || '#6b7280';

                return (
                  <div
                    key={group.key}
                    className={`documents-client-group cmp-group${isOpen ? ' is-open' : ''}`}
                  >
                    <button
                      type="button"
                      className="documents-client-header cmp-group-head"
                      onClick={() => toggleGroup(group)}
                    >
                      <span className="documents-client-chevron">
                        {isOpen ? <HiChevronDown size={20} /> : <HiChevronRight size={20} />}
                      </span>
                      <div className="documents-client-info">
                        <strong>
                          Invoice <span className="documents-invoice-code">{group.label}</span>
                        </strong>
                        <div className="documents-invoice-title-meta">
                          <span>
                            <span className="documents-meta-label">Destino:</span> {group.destino}
                          </span>
                          <span className="documents-meta-sep">·</span>
                          <span
                            className="documents-status-pill"
                            style={{
                              color: statusColor,
                              background: `${statusColor}18`,
                              borderColor: `${statusColor}40`
                            }}
                          >
                            {statusLabel}
                          </span>
                        </div>
                      </div>
                      <div className="cmp-head-stats">
                        <span className="cmp-head-stat">
                          <strong>{linhas.length}</strong>
                          linhas
                        </span>
                        <span className="cmp-head-stat">
                          <strong>{availableCount}</strong>
                          livres
                        </span>
                        <span className={`cmp-head-stat${invCount === linhas.length && linhas.length ? ' is-ok' : ''}`}>
                          <strong>{`${invCount}/${linhas.length || 0}`}</strong>
                          INV
                        </span>
                        <span className={`cmp-head-stat${nfCount === linhas.length && linhas.length ? ' is-ok' : ''}`}>
                          <strong>{`${nfCount}/${linhas.length || 0}`}</strong>
                          NF
                        </span>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="cmp-body">
                        <div className="cmp-doc">
                          <div className="cmp-doc__bar">
                            <div>
                              <p className="cmp-kicker">Invoice compilada</p>
                              <h3>{group.label}</h3>
                              <span>
                                {`${origemRows.length - availableCount} de ${origemRows.length} itens de origem já lançados`}
                              </span>
                            </div>
                            <div className="cmp-doc__actions">
                              {canCompile ? (
                                <button
                                  type="button"
                                  className="cmp-primary"
                                  onClick={() => openCompose(group)}
                                >
                                  <HiPlus size={16} />
                                  Nova linha
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="cmp-xls"
                                onClick={() => downloadXls(group)}
                                disabled={linhas.length === 0}
                              >
                                <HiDownload size={16} />
                                Baixar XLS
                              </button>
                            </div>
                          </div>
                          <div className="cmp-progress__track" aria-hidden="true">
                            <i style={{ width: `${compiledPct}%` }} />
                          </div>
                          {board?.loading && linhas.length === 0 ? (
                            <p className="cmp-empty">Carregando linhas…</p>
                          ) : linhas.length === 0 ? (
                            <div className="cmp-doc-empty">
                              <p>
                                {canCompile
                                  ? 'Ainda não há linhas. Clique em Nova linha para selecionar os itens e montar a invoice.'
                                  : 'O administrador ainda não compilou esta invoice.'}
                              </p>
                            </div>
                          ) : (
                            <>
                              <div className="cmp-doc__cols">
                                <span>#</span>
                                <span>INV / NF</span>
                                <span>Descrição</span>
                                <span>NCM</span>
                                <span className="is-num">Qtd</span>
                                <span className="is-num">Peso</span>
                                <span className="is-num">Preço</span>
                                <span>CFOP</span>
                                <span className="is-num">Tributável</span>
                                <span className="is-actions">Ações</span>
                              </div>
                              <ol className="cmp-doc-lines">
                                {linhas.map((linha) => {
                                  const editing = editingId === linha.id;
                                  const nfLocked = !linha.inv_ok;
                                  const tributavel = editing
                                    ? calcValorUnitarioTributavel(
                                        editForm.valor_unitario,
                                        editForm.peso_unitario
                                      )
                                    : linha.valor_unitario_tributavel;
                                  const q = parseNumber(linha.quantidade) ?? 1;
                                  const v = parseNumber(linha.valor_unitario);
                                  const totalLinha = v == null ? null : v * q;
                                  return (
                                    <li
                                      key={linha.id}
                                      className={`cmp-doc-line${linha.nf_ok && linha.inv_ok ? ' is-done' : linha.inv_ok ? ' is-partial' : ' is-alert'}${editing ? ' is-editing' : ''}`}
                                    >
                                      <div className="cmp-doc-line__head">
                                        <span className="cmp-line__num">{linha.item_numero}</span>
                                        <div className="cmp-flags">
                                          <label className={`documents-flag documents-flag-inv${linha.inv_ok ? ' is-on' : ''}`}>
                                            <input
                                              type="checkbox"
                                              checked={!!linha.inv_ok}
                                              disabled={!isAdmin}
                                              onChange={(e) =>
                                                toggleFlag(linha, 'inv_ok', e.target.checked)
                                              }
                                            />
                                            INV
                                          </label>
                                          <label
                                            className={`documents-flag${linha.nf_ok ? ' is-on' : ''}`}
                                            title={
                                              nfLocked
                                                ? 'Marque INV antes de marcar NF'
                                                : 'Já lançou na NF'
                                            }
                                          >
                                            <input
                                              type="checkbox"
                                              checked={!!linha.nf_ok}
                                              disabled={nfLocked || (!isAdmin && !isContador)}
                                              onChange={(e) =>
                                                toggleFlag(linha, 'nf_ok', e.target.checked)
                                              }
                                            />
                                            NF
                                          </label>
                                        </div>
                                      </div>
                                      {editing ? (
                                        <div className="cmp-line-edit cmp-doc-edit">
                                          <input
                                            className="cmp-line-edit__desc"
                                            value={editForm.descricao}
                                            onChange={(e) =>
                                              setEditForm((f) => ({ ...f, descricao: e.target.value }))
                                            }
                                          />
                                          <div className="cmp-fields">
                                            <label className="cmp-field">
                                              Qtd
                                              <input
                                                value={editForm.quantidade}
                                                onChange={(e) =>
                                                  setEditForm((f) => ({
                                                    ...f,
                                                    quantidade: e.target.value
                                                  }))
                                                }
                                              />
                                            </label>
                                            <label className="cmp-field">
                                              Preço
                                              <input
                                                value={editForm.valor_unitario}
                                                onChange={(e) =>
                                                  setEditForm((f) => ({
                                                    ...f,
                                                    valor_unitario: e.target.value
                                                  }))
                                                }
                                              />
                                            </label>
                                            <label className="cmp-field">
                                              Peso
                                              <input
                                                value={editForm.peso_unitario}
                                                onChange={(e) =>
                                                  setEditForm((f) => ({
                                                    ...f,
                                                    peso_unitario: e.target.value
                                                  }))
                                                }
                                              />
                                            </label>
                                            <label className="cmp-field">
                                              NCM
                                              <input
                                                value={editForm.ncm}
                                                onChange={(e) =>
                                                  setEditForm((f) => ({ ...f, ncm: e.target.value }))
                                                }
                                              />
                                            </label>
                                            <label className="cmp-field">
                                              CFOP
                                              <input
                                                value={editForm.cfop}
                                                onChange={(e) =>
                                                  setEditForm((f) => ({ ...f, cfop: e.target.value }))
                                                }
                                              />
                                            </label>
                                          </div>
                                          <div className="cmp-chaves">
                                            {chaves.map((c) => (
                                              <button
                                                type="button"
                                                key={c.chave}
                                                className={`cmp-chave${editForm.chave_acesso === c.chave ? ' is-on' : ''}`}
                                                onClick={() => pickChave(setEditForm, chaves, c.chave)}
                                              >
                                                <b>{shortChave(c.chave)}</b>
                                              </button>
                                            ))}
                                          </div>
                                          <small>{`Tributável ${displayTributavel(tributavel)}`}</small>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="cmp-doc-line__main">
                                            <strong>{linha.descricao || 'Sem descrição'}</strong>
                                            {linha.chave_acesso ? (
                                              <code className="cmp-doc-chave">{linha.chave_acesso}</code>
                                            ) : (
                                              <em>Sem chave de acesso</em>
                                            )}
                                          </div>
                                          <div className="cmp-doc-facts">
                                            <span className="cmp-doc-ncm" data-label="NCM">
                                              {linha.ncm || '—'}
                                            </span>
                                            <span className="cmp-doc-numval cmp-doc-qty" data-label="Qtd">
                                              {displayQty(linha.quantidade)}
                                            </span>
                                            <span className="cmp-doc-numval cmp-doc-peso" data-label="Peso">
                                              {displayWeight(linha.peso_unitario)}
                                            </span>
                                            <span className="cmp-doc-numval cmp-doc-preco" data-label="Preço">
                                              {displayCurrency(linha.valor_unitario)}
                                              {totalLinha != null && q !== 1 ? (
                                                <small>{displayCurrency(totalLinha)}</small>
                                              ) : null}
                                            </span>
                                            <span className="cmp-doc-cfop" data-label="CFOP">
                                              {linha.cfop || '—'}
                                            </span>
                                            <span className="cmp-doc-numval cmp-doc-trib" data-label="Tributável">
                                              {displayTributavel(tributavel)}
                                            </span>
                                          </div>
                                        </>
                                      )}
                                      <div className="cmp-doc-line__side">
                                        {isAdmin ? (
                                          <div className="cmp-line__acts">
                                            {editing ? (
                                              <>
                                                <button
                                                  type="button"
                                                  className="btn-icon btn-view"
                                                  title="Salvar"
                                                  aria-label="Salvar"
                                                  disabled={saving}
                                                  onClick={() => saveLinha(linha)}
                                                >
                                                  <HiCheck size={18} />
                                                  <span className="cmp-act-label">Salvar</span>
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn-icon"
                                                  title="Cancelar"
                                                  aria-label="Cancelar"
                                                  onClick={() => setEditingId(null)}
                                                >
                                                  <HiX size={18} />
                                                  <span className="cmp-act-label">Cancelar</span>
                                                </button>
                                              </>
                                            ) : (
                                              <>
                                                <button
                                                  type="button"
                                                  className="btn-icon btn-edit"
                                                  title="Editar"
                                                  aria-label="Editar"
                                                  onClick={() => {
                                                    setEditingId(linha.id);
                                                    setEditForm(linhaToForm(linha));
                                                  }}
                                                >
                                                  <HiPencil size={16} />
                                                  <span className="cmp-act-label">Editar</span>
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn-icon btn-delete"
                                                  title="Excluir"
                                                  aria-label="Excluir"
                                                  onClick={() => setDeleteTarget(linha)}
                                                >
                                                  <HiTrash size={16} />
                                                  <span className="cmp-act-label">Excluir</span>
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="cmp-doc-numval">—</span>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                              <div className="cmp-doc__foot">
                                <span>{`${linhas.length} linha${linhas.length !== 1 ? 's' : ''}`}</span>
                                <strong>{`Total ${displayCurrency(valorTotal)}`}</strong>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="table-footer">
              <div className="pagination-info">
                {filteredGroups.length > 0 ? (
                  <span>
                    {`Mostrando ${startIndex + 1} a ${Math.min(
                      startIndex + perPage,
                      filteredGroups.length
                    )} de ${filteredGroups.length} invoice${filteredGroups.length !== 1 ? 's' : ''}`}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className="items-per-page">
                <span>Mostrar:</span>
                <select
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="pagination">
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  ‹
                </button>
                <span className="pagination-info">
                  {`Página ${page} de ${totalPages}`}
                </span>
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        type="danger"
        title="Excluir linha compilada"
        message="Os itens de origem voltam a ficar disponíveis para uma nova compilação."
        confirmText="Excluir"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        isOpen={Boolean(invClearTarget)}
        type="warning"
        title="Desmarcar INV"
        message="Desmarcar INV também desmarca NF nesta linha. A conferência e a edição ficam pendentes até os checks serem feitos de novo."
        confirmText="Desmarcar INV"
        onConfirm={confirmClearInv}
        onCancel={() => setInvClearTarget(null)}
      />

      {composeKey
        ? (() => {
            const group = invoiceGroups.find((g) => g.key === composeKey);
            if (!group) return null;
            const {
              invoiceId,
              board,
              origemRows,
              used,
              selected,
              selectedRows,
              cxGroups,
              visibleRows,
              grouped,
              allVisibleAvailable,
              allAvailableChecked,
              chavesSelecao,
              composerOpen,
              tributavelDraft
            } = getCompileState(group);
            return (
              <div className={`cmp-fs cmp-fs--s${composeStep}`} role="dialog" aria-modal="true" aria-label="Compilar linha da invoice">
                <div className="cmp-fs__bar">
                  <div>
                    <p className="cmp-kicker">Nova linha</p>
                    <h2>{`Invoice ${group.label}`}</h2>
                    <span className="cmp-fs__hint">Passo 1: marque os itens · Passo 2: confira a linha e adicione</span>
                  </div>
                  <div className="cmp-fs__steps" aria-hidden="true">
                    <b className={composeStep === 1 ? 'is-on' : ''}>1</b>
                    <i />
                    <b className={composeStep === 2 ? 'is-on' : ''}>2</b>
                  </div>
                  <button type="button" className="cmp-fs__close" onClick={closeCompose}>
                    <HiX size={22} />
                    Fechar
                  </button>
                </div>
                <div className="cmp-fs__grid">
                  <section className="cmp-col cmp-col--pick">
                    <header className="cmp-col__head">
                      <div>
                        <p className="cmp-kicker">Passo 1</p>
                        <h3>Marque o que entra nesta linha</h3>
                      </div>
                      <label className="cmp-switch">
                        <input
                          type="checkbox"
                          checked={hideUsed}
                          onChange={(e) => setHideUsed(e.target.checked)}
                        />
                        Só disponíveis
                      </label>
                    </header>
                    <div className="cmp-cx-tabs" role="tablist" aria-label="Filtrar por caixa">
                      <button
                        type="button"
                        className={`cmp-cx-tab${cxTab === 'all' ? ' is-on' : ''}`}
                        onClick={() => setCxTab('all')}
                      >
                        {`Todas · ${origemRows.length}`}
                      </button>
                      {cxGroups.map(({ box, rows }) => {
                        const livres = rows.filter(({ item }) => !used.has(Number(item.id))).length;
                        return (
                          <button
                            type="button"
                            key={boxKey(box)}
                            className={`cmp-cx-tab${cxTab === boxKey(box) ? ' is-on' : ''}`}
                            onClick={() => setCxTab(boxKey(box))}
                          >
                            {`${box.codigo_pacote || 'CX'} · ${livres}`}
                          </button>
                        );
                      })}
                    </div>
                    <div className="cmp-pick-tools">
                      <div className="cmp-mini-search">
                        <HiSearch size={16} />
                        <input
                          type="text"
                          placeholder="Buscar descrição ou NCM"
                          value={itemQuery}
                          onChange={(e) => setItemQuery(e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="cmp-ghost"
                        disabled={allVisibleAvailable.length === 0}
                        onClick={() =>
                          setBoxSelection(
                            invoiceId,
                            group,
                            visibleRows,
                            used,
                            !allAvailableChecked
                          )
                        }
                      >
                        {allAvailableChecked ? 'Desmarcar lista' : 'Marcar toda a lista'}
                      </button>
                    </div>
                    {!invoiceId ? (
                      <p className="cmp-empty">Esta invoice ainda não tem ID numérico.</p>
                    ) : board?.loading && origemRows.length === 0 ? (
                      <p className="cmp-empty">Carregando itens…</p>
                    ) : origemRows.length === 0 ? (
                      <p className="cmp-empty">Nenhum produto nas remessas. Confira a aba Remessas / NF.</p>
                    ) : visibleRows.length === 0 ? (
                      <p className="cmp-empty">Nenhum item disponível neste filtro.</p>
                    ) : (
                      <div className="cmp-pick-list">
                        <div className="cmp-pick-head">
                          <label className="cmp-pick-check">
                            <input
                              type="checkbox"
                              checked={allAvailableChecked}
                              disabled={allVisibleAvailable.length === 0}
                              onChange={() =>
                                setBoxSelection(
                                  invoiceId,
                                  group,
                                  visibleRows,
                                  used,
                                  !allAvailableChecked
                                )
                              }
                            />
                          </label>
                          <span>CX</span>
                          <span>Item</span>
                          <span>Qtd</span>
                          <span>Preço</span>
                          <span>NCM</span>
                        </div>
                        {grouped.map(({ box, rows }) => {
                          const groupAvailable = rows.filter(({ item }) => !used.has(Number(item.id)));
                          const groupChecked =
                            groupAvailable.length > 0 &&
                            groupAvailable.every(({ item }) => selected.has(Number(item.id)));
                          return (
                          <div key={boxKey(box)} className="cmp-pick-group">
                            <div className="cmp-pick-group__title">
                              <button
                                type="button"
                                className="cmp-pick-group__all"
                                disabled={groupAvailable.length === 0}
                                onClick={() =>
                                  setBoxSelection(invoiceId, group, rows, used, !groupChecked)
                                }
                              >
                                <span className={`cmp-pick-tick${groupChecked ? ' is-on' : ''}`} aria-hidden="true" />
                                <strong>{box.codigo_pacote || 'CX'}</strong>
                                <em>{box.cliente?.nome || box.cliente_nome || ''}</em>
                              </button>
                              <span>
                                {`${rows.filter(({ item }) => selected.has(Number(item.id))).length} marcados`}
                              </span>
                            </div>
                            {rows.map((row) => {
                              const itemId = Number(row.item.id);
                              const isUsed = used.has(itemId);
                              const checked = selected.has(itemId);
                              const linhaNum =
                                row.item_numero ||
                                (board?.linhas || []).find((l) =>
                                  (l.produto_ids || []).map(Number).includes(itemId)
                                )?.item_numero;
                              return (
                                <button
                                  type="button"
                                  key={row.key}
                                  disabled={isUsed}
                                  aria-pressed={checked && !isUsed}
                                  className={`cmp-pick-row${checked ? ' is-on' : ''}${isUsed ? ' is-used' : ''}`}
                                  onClick={() => toggleItem(invoiceId, group, itemId, used)}
                                >
                                  <span className={`cmp-pick-tick${checked && !isUsed ? ' is-on' : ''}`} aria-hidden="true" />
                                  <span className="cmp-pick-body">
                                    <span className="cmp-pick-cx">{box.codigo_pacote || 'CX'}</span>
                                    <span className="cmp-pick-desc">
                                      {getItemDescricao(row.item) || 'Sem descrição'}
                                      {isUsed ? <small>{`Já na linha ${linhaNum || '—'}`}</small> : null}
                                    </span>
                                    <span className="cmp-pick-chips">
                                      <span className="cmp-pick-qty" data-label="Qtd">
                                        {displayQty(getItemQty(row.item))}
                                      </span>
                                      <span className="cmp-pick-price" data-label="Preço">
                                        {displayCurrency(getItemValor(row.item))}
                                      </span>
                                      <span className="cmp-pick-ncm" data-label="NCM">
                                        {getItemNcm(row.item) || '—'}
                                      </span>
                                    </span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  <section className={`cmp-sheet${composerOpen ? ' is-live' : ''}`}>
                    <header className="cmp-col__head">
                      <div>
                        <p className="cmp-kicker">Passo 2</p>
                        <h3>Linha da invoice</h3>
                      </div>
                      {composerOpen ? (
                        <span className="cmp-count">{`${selected.size} item${selected.size !== 1 ? 's' : ''}`}</span>
                      ) : null}
                    </header>
                    {!composerOpen ? (
                      <div className="cmp-sheet-empty">
                        <p>Marque os itens no Passo 1. Eles entram nesta linha antes de você salvar.</p>
                      </div>
                    ) : (
                      <>
                        <ul className="cmp-basket">
                          {selectedRows.map((row) => (
                            <li key={row.key}>
                              <b>{row.box?.codigo_pacote || 'CX'}</b>
                              <span>{getItemDescricao(row.item) || 'Sem descrição'}</span>
                              <em>{displayCurrency(getItemValor(row.item))}</em>
                              <button
                                type="button"
                                title="Tirar da linha"
                                onClick={() => toggleItem(invoiceId, group, row.item.id, used)}
                              >
                                <HiX size={14} />
                              </button>
                            </li>
                          ))}
                        </ul>
                        <label className="cmp-field cmp-field--full">
                          Descrição
                          <input
                            value={draft.descricao}
                            onChange={(e) => setDraft((d) => ({ ...d, descricao: e.target.value }))}
                          />
                        </label>
                        <div className="cmp-fields">
                          <label className="cmp-field">
                            Quantidade
                            <input
                              value={draft.quantidade}
                              inputMode="decimal"
                              onChange={(e) =>
                                setDraft((d) => ({ ...d, quantidade: e.target.value }))
                              }
                            />
                          </label>
                          <label className="cmp-field">
                            Preço unit.
                            <input
                              value={draft.valor_unitario}
                              inputMode="decimal"
                              onChange={(e) =>
                                setDraft((d) => ({ ...d, valor_unitario: e.target.value }))
                              }
                            />
                          </label>
                          <label className="cmp-field">
                            Peso unit.
                            <input
                              value={draft.peso_unitario}
                              inputMode="decimal"
                              onChange={(e) =>
                                setDraft((d) => ({ ...d, peso_unitario: e.target.value }))
                              }
                            />
                          </label>
                          <label className="cmp-field">
                            NCM
                            <input
                              value={draft.ncm}
                              onChange={(e) => setDraft((d) => ({ ...d, ncm: e.target.value }))}
                            />
                          </label>
                          <label className="cmp-field">
                            CFOP
                            <input
                              value={draft.cfop}
                              onChange={(e) => setDraft((d) => ({ ...d, cfop: e.target.value }))}
                            />
                          </label>
                          <div className="cmp-tributavel">
                            <span>Tributável</span>
                            <strong>{displayTributavel(tributavelDraft)}</strong>
                          </div>
                        </div>
                        <div className="cmp-chave-block">
                          <span>Chave de acesso da NF</span>
                          {chavesSelecao.length > 0 ? (
                            <>
                              <p>Notas dos itens/cliente selecionados. A mesma chave pode ser usada em várias linhas.</p>
                              <div className="cmp-chaves">
                                {chavesSelecao.map((c) => (
                                  <button
                                    type="button"
                                    key={c.chave}
                                    className={`cmp-chave${draft.chave_acesso === c.chave ? ' is-on' : ''}`}
                                    title={c.chave}
                                    onClick={() => pickChave(setDraft, chavesSelecao, c.chave)}
                                  >
                                    <b>{c.chave}</b>
                                    {c.codigo_pacote ? <small>{c.codigo_pacote}</small> : null}
                                  </button>
                                ))}
                              </div>
                            </>
                          ) : (
                            <>
                              <p>Nenhuma NF encontrada para os itens selecionados. Preencha a chave de acesso.</p>
                              <label className="cmp-field cmp-field--full">
                                Chave de acesso
                                <input
                                  value={draft.chave_acesso}
                                  inputMode="numeric"
                                  autoComplete="off"
                                  maxLength={44}
                                  placeholder="44 dígitos da chave de acesso"
                                  onChange={(e) => {
                                    const digits = normalizeChaveDigits(e.target.value);
                                    setDraft((d) => ({ ...d, chave_acesso: digits, document_id: '' }));
                                  }}
                                />
                              </label>
                              <small className="cmp-chave-count">
                                {`${(draft.chave_acesso || '').length}/44 dígitos`}
                              </small>
                            </>
                          )}
                        </div>
                        <div className="cmp-sheet-actions">
                          <button
                            type="button"
                            className="cmp-ghost"
                            onClick={() => clearSelection(invoiceId)}
                          >
                            Limpar
                          </button>
                          <button
                            type="button"
                            className="cmp-primary"
                            disabled={saving}
                            onClick={() => submitComposer(group)}
                          >
                            <HiPlus size={16} />
                            Adicionar linha
                          </button>
                        </div>
                      </>
                    )}
                  </section>
                </div>
                <div className="cmp-fs__nav">
                  {composeStep === 1 ? (
                    <button
                      type="button"
                      className="cmp-primary"
                      disabled={selected.size === 0}
                      onClick={() => {
                        if (selected.size === 0) {
                          showWarning('Marque pelo menos um item');
                          return;
                        }
                        setComposeStep(2);
                      }}
                    >
                      Continuar{selected.size ? ` · ${selected.size}` : ''}
                    </button>
                  ) : (
                    <>
                      <button type="button" className="cmp-ghost" onClick={() => setComposeStep(1)}>
                        Voltar
                      </button>
                      <button
                        type="button"
                        className="cmp-primary"
                        disabled={saving || !composerOpen}
                        onClick={() => submitComposer(group)}
                      >
                        <HiPlus size={16} />
                        Adicionar linha
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })()
        : null}
    </>
  );
};

export default DocumentsCompilacao;
